import { useEffect, useRef, useState } from "react";
import { payabliConfigCard, payabliConfigACH, payabliConfigWallet } from "./PaymentConfig";
import { getPayabliSrc, isLowerEnvironment } from "../../../utils/utils";
import { handlePaymentSuccess, handlePaymentError, PaymentCallbackProps } from "./PaymentCallbacks";
import { IPaymentObject } from "../../../utils/types";

export const PAYMENT_TABS = {
  CARD: "card",
  BANK_DEBIT: "bank_debit",
  WALLET: "wallet"
};

interface PayabliComponentInstance {
  cleanup?: () => void;
  destroy?: () => void;
  updateConfig: (config: any) => void;
  payabliExec: (method: string, data: any) => void;
}

export const usePayabliHook = (
  paymentObject: IPaymentObject,
  onSuccess?: (message: string, referenceId?: string) => void,
  onError?: (message: string) => void,
  closePopup?: () => void,
  paymentType?: string
) => {
  const [selectedPaymentTab, setSelectedPaymentTab] = useState(PAYMENT_TABS.CARD);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const payabliComponentRef = useRef<PayabliComponentInstance | null>(null);
  const isInitializedRef = useRef(false);
  const callbackExecutedRef = useRef(false);

  // Global flag to prevent multiple Payabli instances
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (!window.payabliInstanceActive) {
      // @ts-ignore
      window.payabliInstanceActive = false;
    }
  }

  // Comprehensive cleanup function
  const cleanupPayabliComponent = () => {
    if (payabliComponentRef.current) {
      try {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          // @ts-ignore
          payabliComponentRef.current.cleanup();
        }
        
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          // @ts-ignore
          payabliComponentRef.current.destroy();
        }
      } catch (error) {
        console.error('Error cleaning up Payabli component:', error);
      }
      payabliComponentRef.current = null;
    }
    
    // Try to clean up any global Payabli references
    try {
      // Remove any Payabli-related DOM elements
      // const payabliElements = document.querySelectorAll('[id*="pay-component"], [class*="payabli"], [data-payabli]');
      // payabliElements.forEach((element) => {
      //   console.log('🔍 [DEBUG] removing element', element);
      //   element.remove();
      // });
      
      // Try to clear any global Payabli variables
      if (typeof window !== 'undefined') {
        // @ts-ignore
        if (window.PayabliComponent) {
          // @ts-ignore
          delete window.PayabliComponent;
        }
      }
    } catch (error) {
      console.error('Error during global cleanup:', error);
    }
    
    // Reset initialization flag
    isInitializedRef.current = false;
    callbackExecutedRef.current = false;
    if (typeof window !== 'undefined') {
      (window as any).payabliInstanceActive = false;
    }
  };

  // Get configuration for current payment tab
  const getCurrentConfig = () => {
    let payabliConfig: any = {};
    
    if(selectedPaymentTab === PAYMENT_TABS.CARD) {
      payabliConfig = { ...payabliConfigCard };
    } else if(selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT) {
      payabliConfig = { ...payabliConfigACH };
          } else {
        payabliConfig = { ...payabliConfigWallet };
        if (payabliConfig.expressCheckout) {
          payabliConfig.expressCheckout.amount = paymentObject.amount;
        }
      }
    
    payabliConfig.token = paymentObject.token;
    payabliConfig.entryPoint = paymentObject.entityId;
    payabliConfig.amount = paymentObject.amount;
    payabliConfig.customerData = {
      firstName: paymentObject?.donationData?.firstName,
      lastName: paymentObject?.donationData?.lastName,
      billingEmail: paymentObject?.donationData?.email,
      customerNumber: paymentObject?.donationData?.email.split("@")[0],
      phoneNumber: paymentObject?.donationData?.phoneNumber,
      address: paymentObject?.donationData?.address,
      additionalData: {
        paymentIdentityId: paymentObject.identityId,
        ...paymentObject.donationData,
        firstName: paymentObject?.donationData?.firstName,
        lastName: paymentObject?.donationData?.lastName,
        billingEmail: paymentObject?.donationData?.email,
        customerNumber: paymentObject?.donationData?.email.split("@")[0]
      }
    };

    // Add event listeners for form validation using Payabli's official callback
    payabliConfig.functionCallBackReady = (data: any) => { 
      const isFormValid = data && data[1] === true;
      setIsFormValid(isFormValid);
    };

    payabliConfig.functionCallBackSuccess = (response: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      
      callbackExecutedRef.current = true;
      setIsProcessing(false);
      
      // Reset callback flag after 5 seconds to allow for retries
      setTimeout(() => {
        callbackExecutedRef.current = false;
      }, 5000);
      

      
      // Handle payment success with backend API call
      const callbackProps: PaymentCallbackProps = {
        paymentObject,
        closePopup: () => {
          closePopup?.();
          if (onSuccess) {
            // Extract reference ID from response if available
            // Handle both nested (wallet) and direct (card/ACH) response structures
            const referenceId = response?.data?.responseData?.referenceId || 
                               response?.responseData?.referenceId || 
                               response?.data?.referenceId ||
                               response?.referenceId || '';
            onSuccess('Payment processed successfully!', referenceId);
          }
        },
        onSuccess: () => {
          console.log('Payment processed successfully');
        },
        onError: (error: any) => {
          console.error('Error in success callback:', error);
          closePopup?.();
          if (onError) {
            onError('Payment was unsuccessful. Please try again.');
          }
        }
      };
      
      handlePaymentSuccess(response, callbackProps);
    };

    payabliConfig.functionCallBackError = (errors: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      
      callbackExecutedRef.current = true;
      setIsProcessing(false);
      setIsFormValid(false);
      
      // Reset callback flag after 5 seconds to allow for retries
      setTimeout(() => {
        callbackExecutedRef.current = false;
      }, 5000);
      
      // Handle payment error with backend API call
      const callbackProps: PaymentCallbackProps = {
        paymentObject,
        closePopup: () => {
          closePopup?.();
          if (onError) {
            onError('Payment failed. Please try again.');
          }
        },
        onError: (error: any) => {
          console.error('Error in error callback:', error);
          closePopup?.();
          if (onError) {
            onError('Payment failed. Please try again.');
          }
        }
      };
      
      handlePaymentError(errors, callbackProps);
    };

    return payabliConfig;
  };

  const initializePayabliForm = () => {
    // Check global flag to prevent multiple instances
    if (typeof window !== 'undefined' && (window as any).payabliInstanceActive) {
      return;
    }
    
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }

    const payabliConfig = getCurrentConfig();

    // @ts-ignore
    payabliComponentRef.current = new PayabliComponent(payabliConfig);
    
    // DEBUG: Log payment execution details
    const paymentMethod = selectedPaymentTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
    const paymentExecData = {
      method: paymentMethod,
      paymentDetails: {
        totalAmount: paymentObject.amount,
        serviceFee: 0,
        categories: [{ label: "payment", amount: paymentObject.amount, qty: 1 }],
      }
    };


    // @ts-ignore
    payabliComponentRef.current.payabliExec(paymentMethod, paymentExecData);

    isInitializedRef.current = true;
    if (typeof window !== 'undefined') {
      (window as any).payabliInstanceActive = true;
    }
  };

  // Update configuration when tab changes (without recreating component)
  const updatePaymentTab = (newTab: string) => {
    setSelectedPaymentTab(newTab);
    
    // Reset form validation state when tab changes
    setIsFormValid(false);
    setIsProcessing(false);
    callbackExecutedRef.current = false;

    // If component is already initialized, update the configuration
    if (payabliComponentRef.current && isInitializedRef.current) {
      // Create config for the new tab directly instead of using getCurrentConfig()
      let newConfig: any = {};
      
      if(newTab === PAYMENT_TABS.CARD) {
        newConfig = { ...payabliConfigCard };
      } else if(newTab === PAYMENT_TABS.BANK_DEBIT) {
        newConfig = { ...payabliConfigACH };
      } else {
        newConfig = { ...payabliConfigWallet };
        if (newConfig.expressCheckout) {
          newConfig.expressCheckout.amount = paymentObject.amount;
        }
      }
      
      newConfig.token = paymentObject.token;
      newConfig.entryPoint = paymentObject.entityId;
      newConfig.amount = paymentObject.amount;

      if(paymentType === "DONATION") {
        newConfig.customerData = {
          firstName: paymentObject?.donationData?.firstName,
          lastName: paymentObject?.donationData?.lastName,
          billingEmail: paymentObject?.donationData?.email,
          customerNumber: paymentObject?.donationData?.email.split("@")[0],
          phoneNumber: paymentObject?.donationData?.phoneNumber,
          address: paymentObject?.donationData?.address,
          additionalData: {
            paymentIdentityId: paymentObject.identityId,
            ...paymentObject.donationData,
            firstName: paymentObject?.donationData?.firstName,
            lastName: paymentObject?.donationData?.lastName,
            billingEmail: paymentObject?.donationData?.email,
            customerNumber: paymentObject?.donationData?.email.split("@")[0]
          }
        };
      }
      // Add event listeners for form validation using Payabli's official callback
      newConfig.functionCallBackReady = (data: any) => { 
        const isFormValid = data && data[1] === true;
        setIsFormValid(isFormValid);
      };

      newConfig.functionCallBackSuccess = (response: any) => {
        // Prevent multiple executions
        if (callbackExecutedRef.current) {
          return;
        }
        
        callbackExecutedRef.current = true;
        setIsProcessing(false);
        
        // Reset callback flag after 5 seconds to allow for retries
        setTimeout(() => {
          callbackExecutedRef.current = false;
        }, 5000);
        

        
        // Handle payment success with backend API call
        const callbackProps: PaymentCallbackProps = {
          paymentObject,
          closePopup: () => {
            closePopup?.();
            if (onSuccess) {
                          // Extract reference ID from response if available
            // Handle both nested (wallet) and direct (card/ACH) response structures
            const referenceId = response?.data?.responseData?.referenceId || 
                               response?.responseData?.referenceId || 
                               response?.data?.referenceId ||
                               response?.referenceId || '';
              onSuccess('Payment processed successfully!', referenceId);
            }
          },
          onSuccess: () => {
            console.log('Payment processed successfully');
          },
          onError: (error: any) => {
            console.error('Error in success callback:', error);
            closePopup?.();
            if (onError) {
              onError('Payment was unsuccessful. Please try again.');
            }
          }
        };
        
        handlePaymentSuccess(response, callbackProps);
      };

      newConfig.functionCallBackError = (errors: any) => {
        // Prevent multiple executions
        if (callbackExecutedRef.current) {
          return;
        }
        
        callbackExecutedRef.current = true;
        setIsProcessing(false);
        setIsFormValid(false);
        
        // Reset callback flag after 5 seconds to allow for retries
        setTimeout(() => {
          callbackExecutedRef.current = false;
        }, 5000);
        
        // Handle payment error with backend API call
        const callbackProps: PaymentCallbackProps = {
          paymentObject,
          closePopup: () => {
            closePopup?.();
            if (onError) {
              onError('Payment failed. Please try again.');
            }
          },
          onError: (error: any) => {
            console.error('Error in error callback:', error);
            closePopup?.();
            if (onError) {
              onError('Payment failed. Please try again.');
            }
          }
        };
        
        handlePaymentError(errors, callbackProps);
      };


      
      try {
        // @ts-ignore
        payabliComponentRef.current.updateConfig(newConfig);
        
        // Execute the payment method for the new tab
        const paymentMethod = newTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
        const paymentExecData = {
          method: paymentMethod,
          paymentDetails: {
            totalAmount: paymentObject.amount,
            serviceFee: 0,
            categories: [{ label: "payment", amount: paymentObject.amount, qty: 1 }],
          }
        };
        

        // @ts-ignore
        payabliComponentRef.current.payabliExec(paymentMethod, paymentExecData);
      } catch (error) {
        console.error('Error updating Payabli config:', error);
      }
    }
  };

  const handlePaymentSubmit = () => {
    if (isFormValid && !isProcessing) {
      setIsLoading(true);
      setIsProcessing(true);
      
      if(paymentObject.isRecurring) {
        // @ts-ignore
        payabliComponentRef.current?.payabliExec("method", {});
      } else {
        // Use correct payment method for wallet payments
        const paymentMethod = selectedPaymentTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
        
        // For wallet payments, update config before execution to refresh the amount
        if (selectedPaymentTab === PAYMENT_TABS.WALLET && payabliComponentRef.current) {
          const updatedConfig = getCurrentConfig();
          try {
            // @ts-ignore
            payabliComponentRef.current.updateConfig(updatedConfig);
          } catch (error) {
            console.error('Error updating wallet config:', error);
          }
        }
        // @ts-ignore
        payabliComponentRef.current?.payabliExec(paymentMethod, {
          paymentDetails: {
            totalAmount: paymentObject.amount,
            serviceFee: 0,
            categories: [{ label: "payment", amount: paymentObject.amount, qty: 1 }],
          },
        });
      }
    }
  };

  // First useEffect to mark component as mounted
  useEffect(() => {
    setIsComponentMounted(true);
    
    // Cleanup function for component unmount
    return () => {
      cleanupPayabliComponent();
    };
  }, []);

  // Second useEffect to initialize Payabli after component is mounted (only once)
  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const tag = document.querySelector('script[src*="payabli.com/component.js"]');
    if (!tag) {
      const script = document.createElement("script");
      script.src = getPayabliSrc();
      if(isLowerEnvironment()) {
        script.dataset.test = "true";
      }
      script.onload = () => {
        initializePayabliForm();
      };
      document.body.appendChild(script);
    } else {
      initializePayabliForm();
    }
  }, [isComponentMounted, paymentObject]);

  return {
    selectedPaymentTab,
    setSelectedPaymentTab: updatePaymentTab,
    isFormValid,
    isLoading,
    isProcessing,
    handlePaymentSubmit,
    PAYMENT_TABS
  };
}; 